import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'react-feather';
import Message from './Message';
import styles from './MessageArea.module.css';

function MessageArea({ messages, currentUser, users, channelName, onLoadMore, hasMore, loading, onDeleteMessage }) {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const topSentinelRef = useRef(null);
  const [showNewMessages, setShowNewMessages] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);

  // INTERSECTION Observer for infinite scroll (AC4.2, AC4.3)
  useEffect(() => {
    if (!topSentinelRef.current || !onLoadMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !loading && hasMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(topSentinelRef.current);

    return () => observer.disconnect();
  }, [onLoadMore, hasMore, loading]);

  // CHECK if user is at bottom of scroll
  const checkIfAtBottom = () => {
    if (!messagesContainerRef.current) return true;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 100; // 100px threshold
    setIsAtBottom(atBottom);
    return atBottom;
  };

  // HANDLE scroll event
  const handleScroll = () => {
    checkIfAtBottom();
  };

  // AUTO-SCROLL to bottom when new messages arrive (AC4.11 smart version)
  useEffect(() => {
    if (isAtBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setShowNewMessages(false);
    } else {
      // User scrolled up - show "New messages" button
      if (messages.length > 0) {
        setShowNewMessages(true);
      }
    }
  }, [messages]);

  // SCROLL to bottom when clicking "New messages" button
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowNewMessages(false);
    setIsAtBottom(true);
  };

  // GET channel-specific empty state message
  const getEmptyStateMessage = () => {
    switch (channelName) {
      case 'portal':
        return null;
      case 'afk':
        return 'Stay a while and relax.';
      case 'repo':
        return 'Nothing in the repo yet.';
      case 'general':
      default:
        return 'No messages yet. Start chatting!';
    }
  };

  const emptyMessage = getEmptyStateMessage();

  return (
    <div className={styles.container}>
      <div
        className={styles.messages}
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {/* Top sentinel for infinite scroll (AC4.2) */}
        {hasMore && <div ref={topSentinelRef} className={styles.topSentinel} />}

        {/* Loading indicator (AC4.2) */}
        {loading && (
          <div className={styles.loadingIndicator}>
            <span>Ältere Nachrichten laden...</span>
          </div>
        )}

        {/* Beginning of history indicator (AC4.3) */}
        {!hasMore && messages.length > 0 && (
          <div className={styles.historyStart}>
            <span>━━━ Beginn der #<span className={styles.channelName}>{channelName}</span> Historie ━━━</span>
          </div>
        )}

        {/* Empty state */}
        {messages.length === 0 && emptyMessage && (
          <div className={styles.emptyState}>
            <p>{emptyMessage}</p>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg) => (
          <Message
            key={msg.id}
            message={msg}
            currentUser={currentUser}
            users={users}
            onDelete={onDeleteMessage}
          />
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* New messages indicator (AC4.11) */}
      {showNewMessages && !isAtBottom && (
        <button className={styles.newMessagesButton} onClick={scrollToBottom}>
          <ChevronDown size={16} />
          Neue Nachrichten
        </button>
      )}
    </div>
  );
}

export default MessageArea;
